import React, { useEffect, useState } from 'react';
import { adminAPI } from '../lib/api';
import { toast } from 'sonner';
import { Pause, Play, Trash2 } from 'lucide-react';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all'); // all | user | admin | suspended

  const load = async () => {
    try {
      const { data } = await adminAPI.listUsers();
      setUsers(data.users);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleStatus = async (u) => {
    const next = u.status === 'active' ? 'suspended' : 'active';
    try {
      await adminAPI.setUserStatus(u.id, next);
      toast.success(`User ${next}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This also removes their projects.`)) return;
    try {
      await adminAPI.deleteUser(u.id);
      toast.success('User deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const filtered = users.filter((u) => {
    if (filter === 'user' && u.role !== 'user') return false;
    if (filter === 'admin' && u.role !== 'admin') return false;
    if (filter === 'suspended' && u.status !== 'suspended') return false;
    if (query) {
      const q = query.toLowerCase();
      if (!(u.email || '').toLowerCase().includes(q) && !(u.full_name || '').toLowerCase().includes(q) && !(u.company || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  if (loading)
    return <div className="p-8 text-sm" style={{ color: 'var(--c-ink-2)' }}>Loading users…</div>;

  return (
    <div className="p-8" data-testid="admin-users">
      <div className="page-header -mx-8 -mt-8 mb-8 px-8 py-6">
        <p className="eyebrow">Portal administration</p>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif', color: 'var(--c-ink)' }}>
          Users
        </h1>
        <p className="mt-1 text-sm" style={{ color: 'var(--c-ink-2)' }}>
          Every architect and administrator account on the platform.
        </p>
      </div>

      {/* Toolbar */}
      <div className="bento-card p-4 mb-5 flex items-center gap-3 flex-wrap">
        <input
          type="text"
          className="form-input"
          placeholder="Search name, email, or company…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ maxWidth: 360 }}
          data-testid="user-search-input"
        />
        <div className="flex items-center gap-1">
          {['all', 'user', 'admin', 'suspended'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
              className="px-3 py-1.5 text-xs uppercase tracking-[0.14em] rounded-sm transition-colors"
              style={{
                background: filter === f ? 'var(--c-pine)' : 'transparent',
                color: filter === f ? '#fff' : 'var(--c-ink-2)',
                border: '1px solid var(--c-line)',
              }}
            >
              {f}
            </button>
          ))}
        </div>
        <span className="ml-auto text-xs" style={{ color: 'var(--c-ink-3)' }}>
          {filtered.length} of {users.length}
        </span>
      </div>

      <div className="bento-card p-0 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="px-6 py-16 text-center text-sm" style={{ color: 'var(--c-ink-3)' }}>
            No users match your filters.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="data-table-header">
                <th className="text-left px-6 py-3 font-semibold">Name</th>
                <th className="text-left px-6 py-3 font-semibold">Email</th>
                <th className="text-left px-6 py-3 font-semibold">Company</th>
                <th className="text-left px-6 py-3 font-semibold">Role</th>
                <th className="text-left px-6 py-3 font-semibold">Status</th>
                <th className="text-right px-6 py-3 font-semibold">Projects</th>
                <th className="text-right px-6 py-3 font-semibold">Joined</th>
                <th className="text-right px-6 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} style={{ borderTop: '1px solid var(--c-line)' }}>
                  <td className="px-6 py-3" style={{ color: 'var(--c-ink)', fontWeight: 500 }}>{u.full_name || '—'}</td>
                  <td className="px-6 py-3" style={{ color: 'var(--c-ink-2)' }}>{u.email}</td>
                  <td className="px-6 py-3" style={{ color: 'var(--c-ink-2)' }}>{u.company || '—'}</td>
                  <td className="px-6 py-3">
                    <span className={`badge ${u.role === 'admin' ? 'badge-info' : 'badge-success'}`}>{u.role}</span>
                  </td>
                  <td className="px-6 py-3">
                    <span className={`badge ${u.status === 'active' ? 'badge-success' : 'badge-warning'}`}>{u.status}</span>
                  </td>
                  <td className="px-6 py-3 text-right metric">{u.project_count}</td>
                  <td className="px-6 py-3 text-right text-xs" style={{ color: 'var(--c-ink-3)' }}>
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-6 py-3 text-right">
                    {u.role !== 'admin' ? (
                      <>
                        <button className="p-1.5 rounded hover:bg-[var(--c-bone)] mr-1" title={u.status === 'active' ? 'Suspend' : 'Activate'} onClick={() => toggleStatus(u)} data-testid={`toggle-user-${u.id}`}>
                          {u.status === 'active'
                            ? <Pause className="w-4 h-4" style={{ color: 'var(--c-ink-2)' }} strokeWidth={1.75} />
                            : <Play className="w-4 h-4" style={{ color: 'var(--c-pine)' }} strokeWidth={1.75} />}
                        </button>
                        <button className="p-1.5 rounded hover:bg-[#FCEFEA]" title="Delete" onClick={() => remove(u)} data-testid={`delete-user-${u.id}`}>
                          <Trash2 className="w-4 h-4" style={{ color: 'var(--c-terracotta)' }} strokeWidth={1.75} />
                        </button>
                      </>
                    ) : (
                      <span className="text-xs" style={{ color: 'var(--c-ink-3)' }}>protected</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
